import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, BoltIcon, AcademicCapIcon } from '@heroicons/react/24/outline';
import { studyService } from '../services/studyService';
import { deckService } from '../services/deckService';
import { t } from '../utils/translations';

interface QuickStudyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const QuickStudyModal: React.FC<QuickStudyModalProps> = ({ isOpen, onClose }) => {
  const [cardLimit, setCardLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  console.log('QuickStudyModal render - isOpen:', isOpen);

  // Check if user has decks when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('Modal opened, checking user decks...');
      setError(null); // Clear any previous errors
      checkUserDecks();
    }
  }, [isOpen]);

  const checkUserDecks = async () => {
    try {
      console.log('Fetching user decks...');
      const decks = await deckService.getDecks();
      console.log('User decks:', decks);
      
      if (!decks || decks.length === 0) {
        console.log('No decks found for user');
        setError('Você precisa criar um deck e adicionar cards antes de poder estudar.');
        return;
      }
      
      // Check if user has any cards
      let totalCards = 0;
      for (const deck of decks) {
        if (deck.cardCount) {
          totalCards += deck.cardCount;
        }
      }
      
      console.log('Total cards across all decks:', totalCards);
      
      if (totalCards === 0) {
        setError('Você precisa adicionar cards aos seus decks antes de poder estudar.');
      }
    } catch (error) {
      console.error('Error checking user decks:', error);
    }
  };

  const handleStartQuickStudy = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { session, cards } = await studyService.startQuickSession(cardLimit);
      
      if (!cards || cards.length === 0) {
        setError('Nenhum card disponível para estudo no momento. Crie alguns cards ou aguarde até que estejam prontos para revisão.');
        return;
      }
      
      // Navigate to study page with session data
      navigate('/study', { 
        state: { 
          sessionId: session._id, 
          isQuickStudy: true,
          initialCards: cards,
          totalCards: cards.length
        } 
      });
      
      onClose();
    } catch (err: any) {
      console.error('Error starting quick study:', err);
      setError(err.response?.data?.message || 'Erro ao iniciar estudo rápido. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-gradient-to-br from-success-500 to-success-600 rounded-lg flex items-center justify-center mr-3">
              <BoltIcon className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Estudo Rápido
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-300 text-sm">
            Estude cards de todos os seus decks que estão prontos para revisão. 
            Uma mistura inteligente para maximizar seu aprendizado.
          </p>

          {/* Card limit selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Número de cards
            </label>
            <select
              value={cardLimit}
              onChange={(e) => setCardLimit(Number(e.target.value))}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-gray-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              disabled={loading}
            >
              <option value={5}>5 cards</option>
              <option value={10}>10 cards</option>
              <option value={15}>15 cards</option>
              <option value={20}>20 cards</option>
              <option value={25}>25 cards</option>
            </select>
          </div>

          {/* Features list */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center">
              <AcademicCapIcon className="h-4 w-4 mr-2 text-primary-500" />
              O que você vai estudar:
            </h3>
            <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-1">
              <li>• Cards novos de todos os seus decks</li>
              <li>• Cards prontos para revisão</li>
              <li>• Mistura inteligente para variedade</li>
              <li>• Algoritmo de repetição espaçada</li>
            </ul>
          </div>

          {/* Error message */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex space-x-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            onClick={() => {
              if (!loading) {
                handleStartQuickStudy();
              }
            }}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-gradient-to-r from-success-500 to-success-600 text-white rounded-lg hover:from-success-600 hover:to-success-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Iniciando...
              </>
            ) : (
              <>
                <BoltIcon className="h-4 w-4 mr-2" />
                Começar Estudo
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickStudyModal;
